import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { Controller } from "react-hook-form";
import ComboboxInput from "../inputs/ComboboxInput";
import Accordian from "../accordian";
import { useTranslation } from "react-i18next";
import TextInput from "../inputs/TextInput";
import { useGetAllLocationsDropdown } from "../../hooks/common/query";
import { formattedDate, mapToOptions } from "../../lib/functions";
import { useGetAllAuditLogsReport } from "../../hooks/report/query";
import { EXPORT_PAGE_SIZE } from "../../constants/common";
import { Button } from "../button";
import { ExportAuditLogsReportForExcelPDFBody } from "../../hooks/report/interface";

interface AuditLogsReportCardProps {
  setAllLogs: Dispatch<SetStateAction<never[]>>;
  page: number;
  setPages: Dispatch<SetStateAction<number>>;
  search: string;
  setCount: Dispatch<SetStateAction<number>>;
  setExportBody: Dispatch<
    SetStateAction<ExportAuditLogsReportForExcelPDFBody | undefined>
  >;
  pageSize: number;
  setPage: Dispatch<SetStateAction<number>>;
}

const AuditLogsReportCardSchema = Yup.object({
  visitStartDate: Yup.string(),
  visitEndDate: Yup.string(),
  location: Yup.string(),
  building: Yup.string(),
});

const initialState = {
  visitStartDate: "",
  visitEndDate: "",
  location: "",
  building: "",
};

const AuditLogsReportCard = ({
  setAllLogs,
  page,
  setPages,
  search,
  setCount,
  setExportBody,
  pageSize,
  setPage,
}: AuditLogsReportCardProps) => {
  const {
    control,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useFormWithSchema(AuditLogsReportCardSchema, {
    defaultValues: {
      visitStartDate: formattedDate(new Date()),
      // location:
      //   user?.n_RoleID !== USER_ROLES_IDS.ADMIN
      //     ? user?.n_LocationID?.toString()
      //       ? user?.n_LocationID?.toString()
      //       : ""
      //     : "",
      location: "",
    },
  });
  const { t } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<string>("");

  const location = watch("location");
  const building = watch("building");
  const fromDate = watch("visitStartDate");
  const toDate = watch("visitEndDate");

  const { data: allLocations } = useGetAllLocationsDropdown();
  // const { data: allBuildings } = useGetAllBuildingsById({
  //   encryptedId: location ? location.toString() : "",
  // });

  // const buildings = useMemo(
  //   () =>
  //     allBuildings
  //       ? mapToOptions(
  //           "s_BuildingName_En",
  //           "s_BuildingName_Ar",
  //           "n_BuildingID",
  //           allBuildings
  //         )
  //       : [],
  //   [allBuildings]
  // );

  const { data: auditLogs } = useGetAllAuditLogsReport({
    locationId: Number(location) || 0,
    n_BuildingID: building ? Number(building) : 0,
    startDate: fromDate,
    ...(toDate && { endDate: toDate }),
    pageSize: pageSize,
    pageNumber: page,
    search: search,
  });

  useEffect(() => {
    setExportBody({
      locationId: Number(location) || 0,
      n_BuildingID: building ? Number(building) : 0,
      startDate: fromDate,
      ...(toDate && { endDate: toDate }),
      pageSize: EXPORT_PAGE_SIZE,
      pageNumber: page,
      search: search,
    });
  }, [building, fromDate, location, page, search, setExportBody, t, toDate]);

  useEffect(() => {
    setPage(1);
  }, [fromDate, location, setPage, toDate]);

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations
      ),
    [allLocations]
  );

  useEffect(() => {
    if (auditLogs) {
      setAllLogs(auditLogs.data || []);
      setPages(auditLogs.pagination?.pages || 1);
      setCount(auditLogs.pagination?.count || 0);
    }
  }, [setAllLogs, setPages, setCount, auditLogs]);

  const onClear = useCallback(() => {
    reset(initialState);
  }, [reset]);

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordian
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2">
            {t("searchFilters")}
          </h1>
        }
      >
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
              <Controller
                name="visitStartDate"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="date"
                    label={t("startDate")}
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                  />
                )}
              />

              <Controller
                name="visitEndDate"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="date"
                    label={t("endDate")}
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                  />
                )}
              />

              <Controller
                name="location"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={locations}
                    value={
                      locations?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      setValue("building", "");
                    }}
                    error={errors?.[name]?.message}
                    label={t("location")}
                  />
                )}
              />

              {/* <Controller
                name="building"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={buildings}
                    value={
                      buildings?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("building")}
                  />
                )}
              /> */}
            </div>
          </form>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>
        </div>
      </Accordian>
    </div>
  );
};

export default React.memo(AuditLogsReportCard);
